import React from 'react';
import {createPortal} from 'react-dom';
import ModalWindow from '../ModalWindow/ModalWindow';
import ModalButton from '../ModalWindow/ModalButton/ModalButton';
import {WAR_COLOR} from '../../../styles/constants';
import {getNetworkSocket} from '../../../utils/networkSocket';
import './BotControlModal.css';
import {useGameUiPortalTarget} from '../../gameUi/GameUiPortal';

/**
 * Подтверждение перехвата управления ботом админом (и его снятия) — открывается
 * двойным нажатием по боту в списке игроков (см. PlayersList).
 *
 * Сама пометка ставится на сервере (TAKE_BOT_CONTROL / RELEASE_BOT_CONTROL):
 * он проверяет права и рассылает всем изменение состояния. Модалка лишь
 * объясняет, что произойдет, — в том числе что перехват второго бота вернет
 * управление первым.
 *
 * Рисуется порталом в корень экрана матча (не в body — там не действует
 * дизайн-система матча): список игроков живет внутри боковой панели, и
 * обычная модалка оказалась бы в ее контексте наложения, под панелью. Держать
 * ее через флаги modalVisibility нельзя — у зрителя всплывающие окна скрыты
 * галочкой, а подтверждение перехвата ему нужно всегда.
 */
const BotControlModal = ({bot, players, userId, lobbyId, isSetupPhase, onClose}) => {
  const portalTarget = useGameUiPortalTarget();

  if (!bot) {
    return null;
  }

  const isControlledByMe = bot.botControlledByUserId === userId;
  const isControlledByOther = Boolean(bot.botControlledByUserId) && !isControlledByMe;
  const myOtherControlledBot = players.find((player) => player.isBot
    && player.id !== bot.id && player.botControlledByUserId === userId);

  const send = (type) => {
    const ws = getNetworkSocket();
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({type, payload: {lobbyId, botPlayerId: bot.id}}));
    }
    onClose();
  };

  let title;
  let note;
  let confirmText = null;
  let confirmType = null;

  if (isControlledByOther) {
    title = `Ботом «${bot.name}» уже управляет другой админ`;
    note = 'Перехватить его можно, только когда тот вернет управление.';
  } else if (isControlledByMe) {
    title = `Вернуть управление ботом «${bot.name}»?`;
    note = 'Бот снова будет ходить сам.';
    confirmText = 'Вернуть';
    confirmType = 'RELEASE_BOT_CONTROL';
  } else if (isSetupPhase) {
    // Стартовые кварталы ботам расставляет сервер своим таймером — перехват
    // сервер до конца расстановки не примет
    title = `Перехватить бота «${bot.name}» пока нельзя`;
    note = 'Стартовые кварталы ботам расставляет сервер. '
      + 'Перехватить управление можно, когда расстановка закончится.';
  } else {
    title = `Перехватить управление ботом «${bot.name}»?`;
    note = myOtherControlledBot
      ? `Сейчас вы управляете ботом «${myOtherControlledBot.name}» — его управление вернется ботам.`
      : 'Вы будете ходить за этого игрока, пока не вернете управление. '
        + 'Если выйдете из матча, бот продолжит ходить сам.';
    confirmText = 'Перехватить';
    confirmType = 'TAKE_BOT_CONTROL';
  }

  return createPortal(
    <ModalWindow handleClose={onClose} className={'bot-control-modal'}>
      <div className={'bot-control-modal_title'}>{title}</div>
      <div className={'bot-control-modal_note'}>{note}</div>
      <div className={'bot-control-modal_buttons'}>
        {confirmType ? (
          <>
            <ModalButton text={confirmText} onClick={() => send(confirmType)}/>
            <ModalButton text={'Отмена'} onClick={onClose} style={{background: WAR_COLOR}}/>
          </>
        ) : (
          <ModalButton text={'Понятно'} onClick={onClose}/>
        )}
      </div>
    </ModalWindow>,
    portalTarget
  );
};

export default BotControlModal;
